/**
 * Variáveis do fluxo — declaradas uma vez, preenchidas pelas perguntas, pela
 * lógica e pelas integrações, lidas com `{{nome}}` nos textos.
 *
 * As do sistema (`primeiro_nome`, `nome`, `email`) vêm da pessoa logada:
 * sempre disponíveis, nunca declaradas, nunca destino de uma resposta.
 */
import { branchesOf, itemsOf, rulesOf } from './blocks';
import type { Block, FlowDoc } from './types';

export const VARIABLE_RE = /^[A-Za-z_][A-Za-z0-9_]{0,39}$/;
export const VARIABLE_MAX = 40;

export const SYSTEM_VARIABLES = [
  { name: 'primeiro_nome', label: 'Primeiro nome de quem conversa' },
  { name: 'nome', label: 'Nome completo' },
  { name: 'email', label: 'E-mail' },
] as const;

const SYSTEM = new Set<string>(SYSTEM_VARIABLES.map((v) => v.name));

export function isSystemVariable(name: string): boolean {
  return SYSTEM.has(name);
}

/** `{{ nome }}` — o mesmo padrão de `templates.py`. */
const TEMPLATE_RE = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;

export function referencedVariables(text: string): string[] {
  return [...new Set([...text.matchAll(TEMPLATE_RE)].map((m) => m[1]!))];
}

/** "Área de atuação" → "area_de_atuacao": sugestão de nome válido a partir de um texto. */
export function toVariableName(text: string): string {
  const ascii = text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  const name = /^[0-9]/.test(ascii) ? `v_${ascii}` : ascii;
  return name.slice(0, VARIABLE_MAX);
}

/** Por que o nome não serve, ou `null`. `current`: o nome que está sendo trocado. */
export function variableNameProblem(name: string, doc: FlowDoc, current?: string): string | null {
  if (!name) return 'Dê um nome à variável.';
  if (!VARIABLE_RE.test(name))
    return 'Use letras sem acento, números e _ (até 40), começando por letra ou _.';
  if (isSystemVariable(name)) return `${name} é preenchida automaticamente — escolha outro nome.`;
  if (name !== current && doc.variables.some((v) => v.name === name))
    return 'Já existe uma variável com esse nome.';
  return null;
}

export function addVariable(doc: FlowDoc, name: string): FlowDoc {
  if (doc.variables.some((v) => v.name === name)) return doc;
  return { ...doc, variables: [...doc.variables, { name }] };
}

// ───────────── onde cada bloco usa variáveis ─────────────

/** Campos de texto que aceitam `{{variável}}`, por tipo (`templates_of` da API). */
const TEMPLATE_FIELDS: { [T in Block['type']]?: string[] } = {
  text: ['text'],
  link: ['label', 'url'],
  input_choice: ['button_label', 'retry_message'],
  input_text: ['placeholder', 'retry_message'],
  input_email: ['placeholder', 'retry_message'],
  input_phone: ['placeholder', 'retry_message'],
  input_number: ['placeholder', 'retry_message'],
  input_date: ['retry_message'],
  input_rating: ['retry_message'],
  set_variable: ['value'],
  ai_handoff: ['question', 'instructions'],
  visit_request: ['topic', 'notes'],
  open: ['label'],
};

/** Campos que guardam o nome de uma variável (não um texto). */
const NAME_FIELDS: { [T in Block['type']]?: string[] } = {
  input_choice: ['variable'],
  input_text: ['variable'],
  input_email: ['variable'],
  input_phone: ['variable'],
  input_number: ['variable'],
  input_date: ['variable'],
  input_rating: ['variable'],
  set_variable: ['variable'],
  visit_request: ['phone_variable', 'result_variable'],
  webhook: ['result_variable'],
};

/** Listas de nomes de variável. */
const NAME_LISTS: { [T in Block['type']]?: string[] } = {
  visit_request: ['availability_variables'],
  webhook: ['variables'],
};

type Loose = Record<string, unknown>;

/** Todos os textos com `{{variáveis}}` do bloco. */
export function templatesOf(block: Block): string[] {
  const b = block as unknown as Loose;
  const out = (TEMPLATE_FIELDS[block.type] ?? []).map((f) => String(b[f] ?? ''));
  if (block.type === 'input_choice') out.push(...itemsOf(block).map((i) => i.label));
  if (block.type === 'condition')
    out.push(...branchesOf(block).flatMap((br) => rulesOf(br).map((r) => r.value)));
  return out;
}

/** Variáveis que o bloco cita pelo nome (destino de resposta, regras, integrações). */
export function namedVariables(block: Block): string[] {
  const b = block as unknown as Loose;
  const out = (NAME_FIELDS[block.type] ?? []).map((f) => String(b[f] ?? ''));
  for (const f of NAME_LISTS[block.type] ?? []) out.push(...((b[f] as string[] | undefined) ?? []));
  if (block.type === 'condition')
    out.push(...branchesOf(block).flatMap((br) => rulesOf(br).map((r) => r.variable)));
  return out.filter(Boolean);
}

/** Quantos blocos usam a variável — o aviso antes de apagar. */
export function variableUsages(doc: FlowDoc, name: string): { groupId: string; blockId: string }[] {
  const out: { groupId: string; blockId: string }[] = [];
  for (const g of doc.groups) {
    for (const b of g.blocks) {
      if (
        namedVariables(b).includes(name) ||
        templatesOf(b).some((t) => referencedVariables(t).includes(name))
      )
        out.push({ groupId: g.id, blockId: b.id });
    }
  }
  return out;
}

function rewriteTemplate(text: string, from: string, to: string): string {
  return text.replace(TEMPLATE_RE, (whole, name: string) => (name === from ? `{{${to}}}` : whole));
}

/** Aplica `fn` a cada texto e a cada nome de variável do bloco. */
function mapVariables(block: Block, text: (t: string) => string, name: (n: string) => string): Block {
  const b: Loose = { ...(block as unknown as Loose) };
  for (const f of TEMPLATE_FIELDS[block.type] ?? []) b[f] = text(String(b[f] ?? ''));
  for (const f of NAME_FIELDS[block.type] ?? []) b[f] = name(String(b[f] ?? ''));
  for (const f of NAME_LISTS[block.type] ?? [])
    b[f] = ((b[f] as string[] | undefined) ?? []).map(name).filter(Boolean);
  if (block.type === 'input_choice') b.items = itemsOf(block).map((i) => ({ ...i, label: text(i.label) }));
  if (block.type === 'condition')
    b.branches = branchesOf(block).map((br) => ({
      ...br,
      rules: rulesOf(br).map((r) => ({ ...r, variable: name(r.variable), value: text(r.value) })),
    }));
  return b as unknown as Block;
}

/**
 * Renomeia a declaração e todas as referências: os campos de variável e o
 * `{{antigo}}` em todos os textos.
 */
export function renameVariable(doc: FlowDoc, from: string, to: string): FlowDoc {
  if (from === to) return doc;
  const text = (t: string) => rewriteTemplate(t, from, to);
  const name = (n: string) => (n === from ? to : n);
  return {
    ...doc,
    variables: doc.variables.map((v) => (v.name === from ? { name: to } : v)),
    groups: doc.groups.map((g) => ({ ...g, blocks: g.blocks.map((b) => mapVariables(b, text, name)) })),
  };
}

/**
 * Apaga a declaração. Os campos que escolhiam a variável ficam vazios (o
 * builder pede outra); o `{{nome}}` nos textos fica — a validação aponta
 * onde, para a pessoa decidir o que escrever no lugar.
 */
export function deleteVariable(doc: FlowDoc, name: string): FlowDoc {
  const keep = (t: string) => t;
  const clear = (n: string) => (n === name ? '' : n);
  return {
    ...doc,
    variables: doc.variables.filter((v) => v.name !== name),
    groups: doc.groups.map((g) => ({ ...g, blocks: g.blocks.map((b) => mapVariables(b, keep, clear)) })),
  };
}
